import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import ReactTable from "react-table";
import { DatePicker } from "antd";
import moment from "moment";
import { Button } from "react-bootstrap";
import Spinner from "components/common/Spinner";
import UserTypes from "components/auth/user-types";
import SearchSelect from "components/common/SearchSelect";
import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";
import {
    closeCustomModal,
    openCustomModal
} from "components/custom/modals";
import {
    fetchMerchandiserInvoicingJobs,
    fetchMerchandiserInvoicingInvoices,
    fetchMerchandiserSingleInvoiceForPDF
} from "api/menu/invoicing/actions";
import {
    fetchAccountManagers,
    fetchMerchandisers
} from "../../../../actions/employeeActions";

const PdfViewerModal = React.lazy(() =>
    import("components/menu/invoicing/merchandiser/pdf-viewer-modal"));

class ListInvoices extends React.Component {
    dateFormat = "YYYY-MM-DD";

    constructor(props) {
        super(props);

        this.state = {
            fromDate: moment().startOf("month"),
            toDate: moment(),
            jobId: null,
            accountManagerId: null,
            merchandiserId: null
        };

        this.handleSelect = this.handleSelect.bind(this);
        this.handleChangeDate = this.handleChangeDate.bind(this);
        this.handleLoad = this.handleLoad.bind(this);
        this.getChangersColumns = this.getChangersColumns.bind(this);
        this.viewReport = this.viewReport.bind(this);

        this.props.fetchMerchandiserInvoicingJobs();
        this.props.fetchMerchandiserInvoicingInvoices();
    }

    componentDidMount() {
        const isAdmin = this.props.user.role.includes(UserTypes.admin);
        if (isAdmin) {
            this.props.fetchAccountManagers();
            this.props.fetchMerchandisers();
        } else if (this.props.user.role.includes(UserTypes.accountManager) && !isAdmin) {
            this.props.fetchAccountManagers();
            this.props.fetchMerchandisers();
        }
    }

    handleSelect = (name, option) => {
        this.setState({
            [name]: option
        });
    };

    handleChangeDate = (name, date)  => {
        this.setState({
            [name]: date
        });
    };

    handleLoad = ()  => {
        const {
            fromDate,
            toDate,
            jobId,
            merchandiserId,
            accountManagerId
        } = this.state;

        const requestData = {};
        if (fromDate) {
            requestData.fromDate = moment(fromDate).format(this.dateFormat);
        }
        if (toDate) {
            requestData.toDate = moment(toDate).format(this.dateFormat);
        }
        if (jobId) {
            requestData.jobId = jobId.value;
        }
        if (merchandiserId) {
            requestData.merchandiserId = merchandiserId.value;
        }
        if (accountManagerId) {
            requestData.accountManagerId = accountManagerId.value;
        }
        this.props.fetchMerchandiserInvoicingInvoices(requestData);
    };

    getChangersColumns = () => {
        const {
            user
        } = this.props;

        const isMerchandiser = user.role.includes(UserTypes.merchandiser);

        const columns = [];

        columns.push({
            id: "show-enter-report",
            Header: "Actions",
            // accessor: "completedDate",
            // accessor: original => moment(original.completedDate).format(this.dateFormat),
            Cell: ({ original }) => (
                <Button
                    className="p-1"
                    size="sm"
                    onClick={() => this.viewReport(original)}
                >
                    View/Print Invoice
                </Button>
            ),
            Filter: () => false,
            width: 120
        });
        columns.push({
            id: "invoiceNum",
            Header: "Invoice #",
            accessor: "invoiceNum",
        });
        columns.push({
            id: "dateIssued",
            Header: "Date",
            width: 150,
            accessor: original => moment(original.dateIssued).format(this.dateFormat),
            Filter: ({ filter, onChange }) => (
                <DatePicker
                    Format={this.dateFormat}
                    value={filter ? filter.value : null}
                    onChange={date => onChange(date)}
                    placeholder=""
                />
            )
        });

        if (!isMerchandiser) {
            columns.push({
                id: "empFullname",
                Header: "Merchandiser",
                accessor: "empFullname",
            });
        }

        columns.push({
            id: "amount",
            Header: "Total Amount",
            accessor: original => (original.amount
                ? Math.round((original.amount + Number.EPSILON) * 100) / 100
                : null),
            Filter: ({ filter, onChange }) => (
                <input
                    onChange={event => onChange(event.target.value)}
                    value={filter ? filter.value : ""}
                    className="form-control"
                    type="number"
                />
            )
        });
        columns.push({
            id: "batchNum",
            Header: "Batch #",
            accessor: "batchNum",
            Filter: ({ filter, onChange }) => (
                <input
                    onChange={event => onChange(event.target.value)}
                    value={filter ? filter.value : ""}
                    className="form-control"
                    type="number"
                />
            )
        });

        return columns.map(value => {
            value.headerClassName = "text-left";
            return value;
        });
    };

    viewReport = (original) => {
        this.props.fetchMerchandiserSingleInvoiceForPDF(original.invoiceId, (pdfData) => {
            const modalObj = {
                id: "view-report",
                modalSettings: {
                    size: "lg",
                    style: {
                        top: 20,
                        width: "calc(100% - 40px)"
                    },
                },
                header: `Invoice: ${original.invoiceNum}`,
                children: (
                    <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                        <PdfViewerModal
                            pdfData={pdfData}
                            name={`${original.invoiceNum}.pdf`}
                        />
                    </React.Suspense>
                ),
                handleClose: () => this.props.closeCustomModal(modalObj)
            };
            this.props.openCustomModal(modalObj);
        });
    };

    render() {
        const {
            jobs,
            merchandisers,
            accountManagers,
            invoices,
            user
        } = this.props;
        const {
            fromDate,
            toDate,
            jobId,
            accountManagerId,
            merchandiserId,
        } = this.state;

        const jobOptions = jobs.map((value) => ({
            label: `${value.jobNumber} - ${value.description}`,
            value: value.id,
        }));
        const accountManagerOptions = accountManagers.map((value) => ({
            label: value.name,
            value: value.id,
        }));
        const merchandisersOptions = merchandisers.map((value) => ({
            label: value.name,
            value: value.id,
        }));

        const isAdmin = user.role.includes(UserTypes.admin);
        const isAccountManager = user.role.includes(UserTypes.accountManager) && !isAdmin;

        return (
            <div>
                <div className="row pt-1 pb-3">
                    {isAdmin &&
                        <div className="col-2">
                        <div className="form-group mb-0">
                            <label className="d-block mb-0 font-weight-bold form-label">
                                Account Manager
                            </label>
                            <SearchSelect
                                className="custom-react-select"
                                isLoading={false}
                                isClearable={true}
                                isSearchable={true}
                                options={accountManagerOptions}
                                value={accountManagerId}
                                onChange={(option) => this.handleSelect("accountManagerId", option)}
                            />
                        </div>
                        <div className="form-group mb-0">
                            <label className="d-block mb-0 font-weight-bold form-label">
                                Merchandiser
                            </label>
                            <SearchSelect
                                className="custom-react-select"
                                isLoading={false}
                                isClearable={true}
                                isSearchable={true}
                                options={merchandisersOptions}
                                value={merchandiserId}
                                onChange={(option) => this.handleSelect("merchandiserId", option)}
                            />
                        </div>
                    </div>
                    }
                    <div className="col-3">
                        <div className="form-group mb-0">
                            <label className="d-block mb-0 font-weight-bold form-label">
                                Job
                            </label>
                            <SearchSelect
                                className="custom-react-select"
                                isLoading={false}
                                isClearable={true}
                                isSearchable={true}
                                options={jobOptions}
                                value={jobId}
                                onChange={(option) => this.handleSelect("jobId", option)}
                            />
                        </div>
                        {isAccountManager &&
                        <div className="form-group mb-0">
                            <label className="d-block mb-0 font-weight-bold form-label">
                                Merchandiser
                            </label>
                            <SearchSelect
                                className="custom-react-select"
                                isLoading={false}
                                isClearable={true}
                                isSearchable={true}
                                options={merchandisersOptions}
                                value={merchandiserId}
                                onChange={(option) => this.handleSelect("merchandiserId", option)}
                            />
                        </div>
                        }
                    </div>
                    <div className="col-2">
                        <div className="form-group mb-0">
                            <label className="d-block mb-0 font-weight-bold form-label">
                                From Date
                            </label>
                            <DatePicker
                                value={fromDate}
                                format={this.dateFormat}
                                onChange={date => this.handleChangeDate("fromDate", date)}
                                className="d-block"
                            />
                        </div>
                        <div className="form-group mb-0">
                            <label className="d-block mb-0 font-weight-bold form-label">
                                To Date
                            </label>
                            <DatePicker
                                value={toDate}
                                format={this.dateFormat}
                                onChange={date => this.handleChangeDate("toDate", date)}
                                className="d-block"
                            />
                        </div>
                    </div>
                    <div className="col-2 d-flex flex-column justify-content-end">
                        <button
                            className="btn btn-primary"
                            onClick={this.handleLoad}
                        >
                            Load
                        </button>
                    </div>
                </div>

                <div className="mb-4">
                    {!(this.props.loading || this.props.accountManagerLoading) || <Spinner />}
                    <ReactTable
                        data={invoices || []}
                        columns={this.getChangersColumns()}
                        headerClassName="table-header"
                        showPagination={false}
                        pageSize={invoices ? Math.max(invoices.length, 20) : 20}
                        resizable
                        className="-striped -highlight"
                        // loading={loading}
                        filterable
                        defaultSorted={[
                            {
                                id: "completedDate",
                                desc: false
                            }
                        ]}
                        defaultFilterMethod={filterCaseInsensitive}
                        getNoDataProps = {() => ({
                            style: {
                                zIndex: 0
                            }
                        })}
                        style={{
                            height: "calc(100vh - 250px)",
                        }}
                    />
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    user: state.auth.user,
    accountManagerLoading: state.invoicing.accountManagerLoading,
    loading: state.invoicing.loading,
    jobs: state.invoicing.jobs,
    invoices: state.invoicing.invoices,
    accountManagers: state.employee.accountManagers,
    merchandisers: state.employee.merchandisers
});

const mapStateToDispatch = dispatch => ({
    fetchMerchandiserInvoicingJobs: bindActionCreators(fetchMerchandiserInvoicingJobs, dispatch),
    fetchMerchandiserInvoicingInvoices: bindActionCreators(fetchMerchandiserInvoicingInvoices, dispatch),
    fetchMerchandiserSingleInvoiceForPDF: bindActionCreators(fetchMerchandiserSingleInvoiceForPDF, dispatch),
    fetchMerchandisers: bindActionCreators(fetchMerchandisers, dispatch),
    fetchAccountManagers: bindActionCreators(fetchAccountManagers, dispatch),

    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch)
});

export default connect(
    mapStateToProps,
    mapStateToDispatch
)(ListInvoices);
